import { useEffect, useRef, useState } from "react";
import { ArrowDownToLine, Globe, Loader2 } from "@digital-twins/design-system/icons";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/Text";
import { usersApi } from "@/lib/api";
import type { BrandLookupResult } from "@/lib/api";
import type {
  BrandfetchImportProps,
  BrandfetchLogoChoice,
} from "@/types/creative-library";

const PREVIEW_FORMATS = ["png", "webp", "jpeg", "jpg", "svg"];

function logoKey(logo: BrandfetchLogoChoice) {
  return `${logo.type}:${logo.theme}`;
}

export function BrandfetchImport({ onApply }: BrandfetchImportProps) {
  const { t } = useTranslation("app");
  const [expanded, setExpanded] = useState(false);
  const [domain, setDomain] = useState("");
  const [result, setResult] = useState<BrandLookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const colors = result
    ? result.colors
        .filter(
          (color) =>
            /^#[0-9a-f]{6}$/i.test(color.hex) &&
            typeof color.type === "string" &&
            color.type.length <= 80,
        )
        .slice(0, 20)
        .map((color) => ({ name: color.type, value: color.hex }))
    : [];
  const logoOptions = result
    ? result.logos.flatMap((logo) => {
        const preview = [...(logo.formats ?? [])]
          .filter((format) => format?.src)
          .sort(
            (a, b) =>
              PREVIEW_FORMATS.indexOf(a.format) -
              PREVIEW_FORMATS.indexOf(b.format),
          )[0];
        return preview && logo.type && logo.theme
          ? [{ type: logo.type, theme: logo.theme, src: preview.src }]
          : [];
      })
    : [];
  const [skippedLogos, setSkippedLogos] = useState<Array<string>>([]);
  const chosenLogos = logoOptions.filter(
    (logo) => !skippedLogos.includes(logoKey(logo)),
  );
  const request = useRef(0);
  useEffect(
    () => () => {
      request.current += 1;
    },
    [],
  );
  async function lookup() {
    const current = ++request.current;
    setLoading(true);
    setError(false);
    setResult(null);
    setSkippedLogos([]);
    try {
      const url = new URL(
        domain.includes("://") ? domain : `https://${domain}`,
      );
      if (
        !["http:", "https:"].includes(url.protocol) ||
        !url.hostname.includes(".") ||
        url.username ||
        url.password
      )
        throw new Error("Invalid domain");
      const brand = await usersApi.lookupBrand(url.hostname);
      if (request.current === current) setResult(brand);
    } catch {
      if (request.current === current) setError(true);
    } finally {
      if (request.current === current) setLoading(false);
    }
  }
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-primary/20 bg-background p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Globe className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <Text size="sm">{t("creativeLibrary.importBrandfetch")}</Text>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {t(
            expanded
              ? "creativeLibrary.hideImport"
              : "creativeLibrary.importFromWebsite",
          )}
        </Button>
      </div>
      {expanded && (
        <>
          <Text size="xs" color="muted">
            {t("creativeLibrary.brandfetchHelp")}
          </Text>
          <Label htmlFor="brand-import-domain">
            {t("creativeLibrary.companyWebsite")}
          </Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="brand-import-domain"
              value={domain}
              inputMode="url"
              placeholder="example.com"
              disabled={loading}
              onChange={(e) => {
                setDomain(e.target.value);
                setResult(null);
                setError(false);
              }}
            />
            <Button
              type="button"
              variant="outline"
              className="shrink-0 gap-2"
              onClick={() => void lookup()}
              disabled={!domain.trim() || loading}
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("creativeLibrary.previewImport")}
            </Button>
          </div>
          {error && (
            <Text size="xs" className="text-destructive" role="alert">
              {t("creativeLibrary.brandfetchFailed")}
            </Text>
          )}
          {result && (
            <div
              className="flex flex-col gap-3 rounded-lg border border-border bg-background p-3"
              data-testid="brandfetch-preview"
            >
              <Text size="sm">{result.name}</Text>
              <Text size="xs" color="muted">
                {result.domain}
              </Text>
              {result.lowConfidence && (
                <Text size="xs" color="muted">
                  {t("creativeLibrary.brandfetchLowConfidence")}
                </Text>
              )}
              <div className="flex flex-wrap gap-2">
                {colors.map((color, index) => (
                  <span
                    key={`${color.value}-${index}`}
                    className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs"
                  >
                    <span
                      className="h-3 w-3 rounded-full border border-border"
                      style={{ backgroundColor: color.value }}
                      aria-hidden="true"
                    />
                    {color.value.toUpperCase()}
                  </span>
                ))}
              </div>
              {logoOptions.length > 0 && (
                <div className="flex flex-col gap-2">
                  <Text size="xs" color="muted">
                    {t("creativeLibrary.brandfetchLogos")}
                  </Text>
                  <div className="flex flex-wrap gap-2">
                    {logoOptions.map((logo, index) => {
                      const key = logoKey(logo);
                      const isChosen = !skippedLogos.includes(key);
                      return (
                        <label
                          key={key}
                          className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2"
                          data-testid={`brandfetch-logo-${index}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChosen}
                            onChange={() =>
                              setSkippedLogos((previous) =>
                                isChosen
                                  ? [...previous, key]
                                  : previous.filter((item) => item !== key),
                              )
                            }
                            data-testid={`brandfetch-logo-checkbox-${index}`}
                          />
                          <span
                            className={`flex h-10 w-16 items-center justify-center rounded ${logo.theme === "light" ? "bg-neutral-900" : "bg-white"}`}
                          >
                            <img
                              src={logo.src}
                              alt={t("creativeLibrary.brandfetchLogoAlt", {
                                type: logo.type,
                              })}
                              className="max-h-8 max-w-14 object-contain"
                            />
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <Button
                type="button"
                variant="secondary"
                className="gap-2 self-start"
                data-testid="brandfetch-apply"
                onClick={() => {
                  onApply(
                    result.name,
                    colors,
                    chosenLogos.length
                      ? {
                          domain: result.domain,
                          logos: chosenLogos.map(({ type, theme }) => ({
                            type,
                            theme,
                          })),
                        }
                      : null,
                  );
                  setExpanded(false);
                  setResult(null);
                }}
              >
                <ArrowDownToLine className="h-4 w-4" />
                {t(
                  chosenLogos.length
                    ? "creativeLibrary.applyBrandfetchWithLogos"
                    : "creativeLibrary.applyBrandfetch",
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
